/**
 * Modal leve (sem dependência de Radix Dialog — o login SPA não o tem). Overlay
 * escurecido + painel centrado, fecha no Escape e no clique fora, trava o scroll
 * do body enquanto aberto. Acessível o suficiente: role=dialog + aria-modal.
 */
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

export function Dialog({ open, onClose, title, description, children, className }: DialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          'relative z-10 w-full max-w-[420px] rounded-[16px] border border-line-2 bg-surface-1 p-6 shadow-2xl',
          className,
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="absolute right-4 top-4 rounded-md p-1 text-ink-4 transition-colors hover:bg-surface-2 hover:text-ink-1"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
        <h2 className="pr-8 text-[17px] font-semibold leading-tight text-ink-1">{title}</h2>
        {description && <p className="mt-1.5 text-[13px] leading-snug text-ink-4">{description}</p>}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}
